import { useTranslation } from 'react-i18next';
import { useCurrentOrganisation } from '#pie/auth';
import {
  type MarketRole,
  useGetContactGroups,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

export interface ContactGroupFormFieldValues {
  contactGroups: string[];
  organisationId?: string;
}

const { FormDropdown, FormMultiDropdown } =
  typedFormFields<ContactGroupFormFieldValues>();

interface ContactGroupsFormFieldsProps {
  excludeContactGroupIds?: string[];
  marketRole?: MarketRole;
  availableOrganisationIds?: string[];
  isLoading?: boolean;
}

export const ContactGroupsFormFields = ({
  excludeContactGroupIds = [],
  marketRole,
  availableOrganisationIds,
  isLoading,
}: ContactGroupsFormFieldsProps) => {
  const { t } = useTranslation();
  const { data: contactGroups, isLoading: isLoadingGroups } =
    useGetContactGroups();
  const { currentAccount } = useCurrentOrganisation();

  const subOrganisations = currentAccount?.subOrganisations;
  const showSubunitDropdown = !!subOrganisations && subOrganisations.length > 1;

  const contactGroupOptions = (contactGroups ?? [])
    .filter((g) => (marketRole ? g.marketRoles.includes(marketRole) : true))
    .filter((g) => !excludeContactGroupIds.includes(g.id))
    .map((g) => ({ label: g.name, value: g.id }));

  const subOrganisationsOptions = (subOrganisations ?? [])
    .filter(
      (s) =>
        !availableOrganisationIds || availableOrganisationIds.includes(s.id),
    )
    .map((s) => ({
      label: s.label,
      value: s.id,
    }));

  return (
    <>
      {showSubunitDropdown && (
        <FormDropdown
          options={subOrganisationsOptions}
          label={t('common.subunit')}
          name="organisationId"
          fullWidth
          isLoading={isLoading}
          rules={{ required: true }}
          placeholder={t(
            'cpr_contact_group_create_contact.subunit.placeholder',
          )}
        />
      )}
      <FormMultiDropdown
        options={contactGroupOptions}
        label={t('common.contact_group')}
        name="contactGroups"
        fullWidth
        isLoading={isLoading || isLoadingGroups}
        rules={{ required: true }}
        placeholder={t(
          'cpr_contact_group_create_contact.contact_group.placeholder',
        )}
      />
    </>
  );
};
